'use client';

import { c as _c } from "react/compiler-runtime";
import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useUserRelationships } from '@/lib/hooks/api/timeline-relationships-hooks';
import { type RelationshipStatus, useRelationshipsStore } from './relationships-store';
export type { RelationshipStatus } from './relationships-store';
export function RelationshipsProvider(t0) {
  const $ = _c(19);
  const {
    initialIds,
    children
  } = t0;
  const queueInitial = useRelationshipsStore(_temp);
  const takePending = useRelationshipsStore(_temp2);
  const setKnownBatch = useRelationshipsStore(_temp3);
  const [batchIds, setBatchIds] = useState(_temp4);
  const flushTimerReference = useRef(null);
  let t1;
  let t2;
  if ($[0] !== initialIds || $[1] !== queueInitial) {
    t1 = () => {
      if (!initialIds || initialIds.length === 0) {
        return;
      }
      queueInitial(initialIds);
    };
    t2 = [initialIds, queueInitial];
    $[0] = initialIds;
    $[1] = queueInitial;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== takePending) {
    t3 = () => {
      if (flushTimerReference.current) {
        return;
      }
      flushTimerReference.current = setTimeout(() => {
        flushTimerReference.current = null;
        const pending = takePending();
        if (pending.length === 0) {
          return;
        }
        setBatchIds(previous => {
          if (previous.length === 0) {
            return toSortedCompat(pending);
          }
          const merged = new Set(previous);
          let changed = false;
          for (const id of pending) {
            if (!merged.has(id)) {
              merged.add(id);
              changed = true;
            }
          }
          if (!changed) {
            return previous;
          }
          return toSortedCompat([...merged]);
        });
      }, 40);
    };
    $[4] = takePending;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const scheduleFlush = t3;
  let t4;
  let t5;
  if ($[6] !== scheduleFlush) {
    t4 = () => {
      const unsubscribe = useRelationshipsStore.subscribe((state_2, previousState) => {
        if (state_2.pendingQueue.length === 0) {
          return;
        }
        if (previousState && previousState.pendingQueue === state_2.pendingQueue) {
          return;
        }
        scheduleFlush();
      });
      if (useRelationshipsStore.getState().pendingQueue.length > 0) {
        scheduleFlush();
      }
      return unsubscribe;
    };
    t5 = [scheduleFlush];
    $[6] = scheduleFlush;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (flushTimerReference.current) {
        clearTimeout(flushTimerReference.current);
        flushTimerReference.current = null;
      }
    };
    t7 = [];
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  const {
    data
  } = useUserRelationships(batchIds, batchIds.length > 0);
  let t8;
  if ($[11] !== data?.relationships || $[12] !== setKnownBatch) {
    t8 = () => {
      const relationships = data?.relationships;
      if (!relationships || typeof relationships !== "object") {
        return;
      }
      setKnownBatch(relationships as Record<string, RelationshipStatus>);
    };
    $[11] = data?.relationships;
    $[12] = setKnownBatch;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = data?.relationships;
  let t10;
  if ($[14] !== setKnownBatch || $[15] !== t9) {
    t10 = [t9, setKnownBatch];
    $[14] = setKnownBatch;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useEffect(t8, t10);
  let t11;
  if ($[17] !== children) {
    t11 = <>{children}</>;
    $[17] = children;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}
function _temp4() {
  return [];
}
function _temp3(state_1) {
  return state_1.setKnownBatch;
}
function _temp2(state_0) {
  return state_0.takePending;
}
function _temp(state) {
  return state.queueInitial;
}
export function useRelationshipStatus(profileId) {
  const $ = _c(6);
  const ensure = useRelationshipsStore(_temp5);
  let t0;
  if ($[0] !== profileId) {
    t0 = state_0 => profileId ? state_0.known[profileId] : undefined;
    $[0] = profileId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const status = useRelationshipsStore(t0);
  let t1;
  let t2;
  if ($[2] !== ensure || $[3] !== profileId) {
    t1 = () => {
      if (profileId) {
        ensure(profileId);
      }
    };
    t2 = [ensure, profileId];
    $[2] = ensure;
    $[3] = profileId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return status;
}
function _temp5(state) {
  return state.ensure;
}